// libs
import * as React from "react";
import {
  Link,
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  InputBase,
  Button,
  Stack,
  Pagination,
  Select,
  MenuItem,
} from "@mui/material";
import moment from "moment";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUp from "@mui/icons-material/ArrowDropUp";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useLocation, Link as RouterLink } from "react-router-dom";
import { AuthenticationContext, ImagesContext } from "../Context";
import { getHeaders } from "../utils/HeaderHelper";
import DatePickerWithLabel from "../common/DatePickerWithLabel";
import LoadingReportsTable from "../shared/LoadingReportsTable";
import EmptyReportsTable from "../shared/EmptyReportsTable";
import BadgeList from "../common/BadgeList";
import SignedEncountersBillingAdvancedFilters from "./SignedEncountersBillingAdvancedFilters";

interface ChipData {
  key: string;
  label: string;
  type: "payer_program" | "creator" | "signer";
}

interface FilterOption {
  id: string;
  name: string;
}

interface FilterOptions {
  payer_programs: FilterOption[];
  creators: FilterOption[];
  signers: FilterOption[];
}

interface ReportRow {
  id: number;
  patient_name: string;
  date_of_birth: string;
  member_unique_id: string;
  enrolled_payer_programs: string;
  created_date: string;
  creator: string;
  signed_date: string;
  signer: string;
  patient_id: number;
  encounter_billing_id: number;
  encounter_billing_uuid: string;
  supply_request_id: number | null;
}

const lastMonthStart = () => moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD");
const lastMonthEnd = () => moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD");

const SignedEncountersBillingReport: React.FC = () => {
  const authenticationSetting = React.useContext(AuthenticationContext);
  const images = React.useContext(ImagesContext);
  const location = useLocation();

  const isInitialMount = React.useRef(true);
  const lastNameDebounce = React.useRef<NodeJS.Timeout | null>(null);


  const { urlPayerId, urlPayerName, urlStartDate, urlEndDate } = React.useMemo(() => {
    const p = new URLSearchParams(location.search);
    return {
      urlPayerId: p.get("payer_id") || "",
      urlPayerName: p.get("payer_name") || "",
      urlStartDate: p.get("start_date") || "",
      urlEndDate: p.get("end_date") || "",
    };
  }, [location.search]);

  // Filters — default to last full calendar month; URL params override on direct navigation
  const [startDate, setStartDate] = React.useState<string>(urlStartDate || lastMonthStart());
  const [endDate, setEndDate] = React.useState<string>(urlEndDate || lastMonthEnd());
  const payerIdFilter = urlPayerId;
  const backHref = urlPayerId ? "/health-navigator-summary-report#billing-summary" : "/reports";
  const [lastName, setLastName] = React.useState<string>("");
  const [debouncedLastName, setDebouncedLastName] = React.useState<string>("");

  // Sort
  const [sortObject, setSortObject] = React.useState<{ field: string; direction: string }>({
    field: "",
    direction: "ascending",
  });

  // Advanced search
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedPayerPrograms, setSelectedPayerPrograms] = React.useState<string[]>([]);
  const [selectedCreators, setSelectedCreators] = React.useState<string[]>([]);
  const [selectedSigners, setSelectedSigners] = React.useState<string[]>([]);
  const [badgeData, setBadgeData] = React.useState<ChipData[]>([]);

  // Data
  const [payerName, setPayerName] = React.useState<string>(urlPayerName);
  const [filterOptions, setFilterOptions] = React.useState<FilterOptions>({
    payer_programs: [],
    creators: [],
    signers: [],
  });
  const [tableData, setTableData] = React.useState<ReportRow[]>([]);
  const [totalCount, setTotalCount] = React.useState<number>(0);
  const [pageCount, setPageCount] = React.useState<number>(0);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [perPage, setPerPage] = React.useState<number>(100);
  const [loading, setLoading] = React.useState<boolean>(false);

  // Debounce last name
  React.useEffect(() => {
    if (lastNameDebounce.current) clearTimeout(lastNameDebounce.current);
    lastNameDebounce.current = setTimeout(() => {
      if (lastName.length === 0 || lastName.length >= 2) {
        setDebouncedLastName(lastName);
        setCurrentPage(1);
      }
    }, 400);
    return () => { if (lastNameDebounce.current) clearTimeout(lastNameDebounce.current); };
  }, [lastName]);

  // Reset page when filters change
  React.useEffect(() => {
    if (isInitialMount.current) return;
    setCurrentPage(1);
  }, [startDate, endDate, selectedPayerPrograms, selectedCreators, selectedSigners, perPage]);

  const prevPayerIdRef = React.useRef(payerIdFilter);
  React.useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else if (prevPayerIdRef.current !== payerIdFilter) {

      setPayerName(urlPayerName);
      setTableData([]);
      setTotalCount(0);
      setPageCount(0);
      setFilterOptions({ payer_programs: [], creators: [], signers: [] });
      if (selectedPayerPrograms.length > 0) setSelectedPayerPrograms([]);
      if (selectedCreators.length > 0) setSelectedCreators([]);
      if (selectedSigners.length > 0) setSelectedSigners([]);
    }
    prevPayerIdRef.current = payerIdFilter;
    getData(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    startDate, endDate, debouncedLastName,
    selectedPayerPrograms, selectedCreators, selectedSigners,
    currentPage, perPage, sortObject, payerIdFilter,
  ]);

  // Sync badges
  React.useEffect(() => {
    const ppMap = Object.fromEntries(filterOptions.payer_programs.map((p) => [p.id, p.name]));
    const creatorMap = Object.fromEntries(filterOptions.creators.map((c) => [c.id, c.name]));
    const signerMap = Object.fromEntries(filterOptions.signers.map((s) => [s.id, s.name]));
    const badges: ChipData[] = [
      ...selectedPayerPrograms.map((id) => ({ key: id, label: ppMap[id] ?? `Program ${id}`, type: "payer_program" as const })),
      ...selectedCreators.map((id) => ({ key: id, label: creatorMap[id] ?? `Creator ${id}`, type: "creator" as const })),
      ...selectedSigners.map((id) => ({ key: id, label: signerMap[id] ?? `Signer ${id}`, type: "signer" as const })),
    ];
    setBadgeData(badges);
  }, [selectedPayerPrograms, selectedCreators, selectedSigners, filterOptions]);

  const handleDeleteBadge = (badge: ChipData) => {
    if (badge.type === "payer_program") setSelectedPayerPrograms((p) => p.filter((id) => id !== badge.key));
    else if (badge.type === "creator") setSelectedCreators((p) => p.filter((id) => id !== badge.key));
    else if (badge.type === "signer") setSelectedSigners((p) => p.filter((id) => id !== badge.key));
  };

  const buildParams = (page: number) => {
    const p = new URLSearchParams({
      start_date: startDate || "",
      end_date: endDate || "",
      last_name: debouncedLastName.length >= 2 ? debouncedLastName : "",
      page: String(page),
      per_page: String(perPage),
      sort_by: sortObject.field,
      sort_direction: sortObject.direction,
    });
    if (payerIdFilter) p.set("payer_id", payerIdFilter);
    selectedPayerPrograms.forEach((id) => p.append("payer_program_ids[]", id));
    selectedCreators.forEach((id) => p.append("creator_ids[]", id));
    selectedSigners.forEach((id) => p.append("signer_ids[]", id));
    return p;
  };

  const getData = (page: number) => {
    setLoading(true);
    fetch(
      `/admin/reports/signed_encounters_billing/report_data?${buildParams(page)}`,
      { method: "GET", headers: getHeaders(authenticationSetting.csrfToken) }
    )
      .then((res) => res.json())
      .then((result) => {
        if (!result.success) {
          console.error(result.error);
        } else {
          const resource = result?.resource || {};
          setTableData(resource.results || []);
          setTotalCount(resource.total_count || 0);
          setPageCount(resource.total_pages || 0);
          if (resource.payer_name) setPayerName(resource.payer_name);
          if (resource.filter_options) setFilterOptions(resource.filter_options);
        }
      })
      .catch((err) => console.error("Failed to fetch report data:", err))
      .finally(() => setLoading(false));
  };

  const selectPage = (page: number) => {
    if (page < 1 || page > pageCount) return;
    setCurrentPage(page);
  };

  const handleExportCsv = () => {
    const params = buildParams(1);
    params.delete("page");
    window.location.href = `/admin/reports/signed_encounters_billing/generate_csv.csv?${params}`;
  };

  const setSortOrder = (field: string, direction: string) => {
    setSortObject({ field, direction });
    setCurrentPage(1);
  };

  const sortIcon = (field: string) => {
    if (sortObject.field === field) {
      return sortObject.direction === "ascending"
        ? <img src={images.sort_ascending_src} width="10" className="sort-icon" alt="Asc" />
        : <img src={images.sort_descending_src} width="10" className="sort-icon" alt="Desc" />;
    }
    return <img src={images.sort_plain_src} width="10" className="sort-icon" alt="Sort" />;
  };

  const encounterLink = (row: ReportRow) =>
    `/patient_reports/${row.patient_id}/patient_encounters/new?encounter_id=${row.encounter_billing_uuid}&view_only=1`;

  const hasBadges = badgeData.length > 0;


  const reportTitle = payerName
    ? `${payerName} Signed Encounters Billing Report`
    : payerIdFilter
      ? "Loading Report…"
      : "Signed Encounters Billing Report";

  return (
    <Box className="report-main-content" sx={{ paddingLeft: "120px !important" }}>
      {/* ── Header ── */}
      <Box className="main-header">
        <Box className="heading-with-back-button-section">
          <Stack direction="row" columnGap="12px">
            <Link component={RouterLink} to={backHref} style={{ textDecoration: "none", display: "flex", alignItems: "center" }}>
              <ArrowBackIosNewIcon
                style={{
                  borderRadius: "3px", padding: "8px", color: "black",
                  boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)", display: "flex",
                  alignItems: "center", justifyContent: "center", cursor: "pointer",
                  transition: "all 0.3s ease", fontSize: "14px",
                }}
              />
            </Link>
            <Typography
              className="main-heading"
              sx={{ fontFamily: "QuicksandMedium, Roboto, Helvetica, Arial, sans-serif", fontWeight: 700 }}
            >
              {reportTitle}
            </Typography>
          </Stack>
        </Box>
      </Box>

      {/* ── Content card ── */}
      <Box className="report-details">
        <Box className="sub-header">
          <Box className="subheading-with-pill-section">
            <Typography className="sub-heading">Report Details</Typography>
            <Box className="count-pill">{totalCount}</Box>
          </Box>

          <Box className="filters-section">
            <Paper className="input-paper">
              <InputBase
                placeholder="Search Last Name"
                inputProps={{ "aria-label": "search last name" }}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
              <SearchIcon sx={{ color: "#707070" }} />
            </Paper>

            <DatePickerWithLabel
              date={startDate}
              setDate={setStartDate}
              initialDate={lastMonthStart()}
              label="Report Period Start Date"
            />
            <DatePickerWithLabel
              date={endDate}
              setDate={setEndDate}
              initialDate={lastMonthEnd()}
              label="Report Period End Date"
            />

            <div
              className="advanced-search-container"
              onClick={loading ? undefined : (e) => setAnchorEl(e.currentTarget)}
              style={{ cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.8 : 1 }}
            >
              <FilterListIcon sx={{ width: 20, height: 20 }} />
              <p className="advanced-search-text">Advanced Search</p>
              {anchorEl ? <ArrowDropUp sx={{ width: 24, height: 24 }} /> : <ArrowDropDownIcon sx={{ width: 24, height: 24 }} />}
              <SignedEncountersBillingAdvancedFilters
                anchorEl={anchorEl}
                setAnchorEl={setAnchorEl}
                payerProgramOptions={filterOptions.payer_programs}
                creatorOptions={filterOptions.creators}
                signerOptions={filterOptions.signers}
                selectedPayerPrograms={selectedPayerPrograms}
                setSelectedPayerPrograms={setSelectedPayerPrograms}
                selectedCreators={selectedCreators}
                setSelectedCreators={setSelectedCreators}
                selectedSigners={selectedSigners}
                setSelectedSigners={setSelectedSigners}
                disabled={loading}
              />
            </div>

            <Button className="export-csv-button" onClick={handleExportCsv} disabled={totalCount === 0}>
              Export as CSV
            </Button>
          </Box>
        </Box>

        {/* ── Table ── */}
        <Box className="table-section">
          {hasBadges && (
            <div className="badge-container" style={{ width: "auto", marginBottom: "20px" }}>
              <BadgeList
                badgeData={badgeData}
                setBadgeData={setBadgeData}
                handleDeleteBadge={handleDeleteBadge}
                background="none"
              />
            </div>
          )}

          {loading ? (
            <LoadingReportsTable isBadgeShowing={hasBadges} />
          ) : (
            <>
              {tableData.length > 0 ? (
                <TableContainer
                  className="report-table-container"
                  style={{ height: hasBadges ? "45vh" : "53.8vh" }}
                >
                  <Table stickyHeader>
                    <TableHead className="table-header">
                      <TableRow sx={{ "& td, & th": { borderBottom: "none" } }}>
                        {/* Sortable Patient Name column */}
                        <TableCell
                          align="left"
                          sx={{ minWidth: "160px", cursor: "pointer" }}
                          onClick={() =>
                            setSortOrder(
                              "patient_name",
                              sortObject.field === "patient_name" && sortObject.direction === "ascending"
                                ? "descending"
                                : "ascending"
                            )
                          }
                        >
                          <div className="row" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <span>Patient Name</span>
                            <span className="sortIndicator">{sortIcon("patient_name")}</span>
                          </div>
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "120px" }}>Date of Birth</TableCell>
                        <TableCell align="left" sx={{ minWidth: "130px" }}>Member Unique ID</TableCell>
                        <TableCell align="left" sx={{ minWidth: "210px" }}>Enrolled Payor Program(s)</TableCell>
                        <TableCell align="left" sx={{ minWidth: "140px" }}>Created Date</TableCell>
                        <TableCell align="left" sx={{ minWidth: "150px" }}>Creator</TableCell>
                        <TableCell align="left" sx={{ minWidth: "140px" }}>Signed Date</TableCell>
                        <TableCell align="left" sx={{ minWidth: "150px" }}>Signer</TableCell>
                        <TableCell align="left" sx={{ minWidth: "170px" }}>Connected Supply Request</TableCell>
                        <TableCell align="left" sx={{ minWidth: "140px" }}>{/* empty */}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tableData.map((row, index) => (
                        <TableRow key={`${row.id}-${index}`} sx={{ "& td, & th": { borderBottom: "none" } }}>
                          <TableCell align="left">
                            <Link
                              href={encounterLink(row)}
                              sx={{ color: "black", textDecorationColor: "black", textDecorationLine: "underline", cursor: "pointer" }}
                            >
                              {row.patient_name}
                            </Link>
                          </TableCell>
                          <TableCell align="left">{row.date_of_birth}</TableCell>
                          <TableCell align="left">{row.member_unique_id}</TableCell>
                          <TableCell align="left">{row.enrolled_payer_programs}</TableCell>
                          {/* Thin vertical separator — body only */}
                          <TableCell align="left" sx={{ borderLeft: "1px solid #e0e0e0", minWidth: "140px" }}>
                            {row.created_date}
                          </TableCell>
                          <TableCell align="left">{row.creator}</TableCell>
                          <TableCell align="left">{row.signed_date}</TableCell>
                          <TableCell align="left">{row.signer}</TableCell>
                          <TableCell align="left">
                            {row.supply_request_id ? (
                              // Intentionally the same destination as "View Encounter" —
                              // supply requests are shown inside the encounter view, and
                              // there is no standalone supply request page to link to.
                              <Link
                                href={encounterLink(row)}
                                sx={{
                                  display: "inline-flex", alignItems: "center", gap: "4px",
                                  color: "#0B34D8", fontSize: "13px", fontWeight: 500,
                                  textDecorationLine: "underline", cursor: "pointer",
                                }}
                              >
                                View Supply Request
                                <OpenInNewIcon sx={{ fontSize: 14 }} />
                              </Link>
                            ) : null}
                          </TableCell>
                          <TableCell align="left">
                            <Link
                              href={encounterLink(row)}
                              sx={{
                                display: "inline-flex", alignItems: "center", gap: "4px",
                                color: "#0B34D8", fontSize: "13px", fontWeight: 500,
                                textDecorationLine: "underline", cursor: "pointer",
                              }}
                            >
                              View Encounter
                              <OpenInNewIcon sx={{ fontSize: 14 }} />
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <EmptyReportsTable isBadgeShowing={hasBadges} />
              )}
            </>
          )}

          {/* Pagination — OneProgramReport style */}
          {tableData.length > 0 && !loading && (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, marginTop: 2 }}>
              <Typography sx={{ fontSize: "14px" }}>Rows per page</Typography>
              <Select
                value={perPage}
                onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}
                disabled={loading}
                size="small"
                sx={{
                  minWidth: 70,
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
                }}
              >
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={150}>150</MenuItem>
                <MenuItem value={200}>200</MenuItem>
              </Select>
              {pageCount > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Pagination
                    count={pageCount}
                    page={currentPage}
                    onChange={(_e, page) => selectPage(page)}
                    boundaryCount={1}
                    siblingCount={1}
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SignedEncountersBillingReport;
